import { Spacing } from '@persona-id/design-tokens';
import { TransferInIcon } from '@persona-id/design-tokens/icons';
import { sortBy } from 'lodash/fp';
import { useState } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { formatBillingDate, formatDollar } from 'dashboard/lib/utils/formatters';
import { Invoice, InvoiceStatus } from 'dashboard/models/invoice';
import { loadStripeInvoicePdfUrl } from 'dashboard/redux/thunk-actions/invoice';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

import DashboardOrganizationInvoiceFinalizedView from '../DashboardOrganizationInvoiceFinalizedView/DashboardOrganizationInvoiceFinalizedView';

interface DashboardOrganizationBillingInvoicesViewProps {
  invoices: Invoice[];
  loadStripeInvoicePdfUrl: (...args: Parameters<typeof loadStripeInvoicePdfUrl>) => Promise<string>;
}

export const InvoiceStatusPill = (props: { invoice: Invoice }) => {
  const invoice = props.invoice;

  switch (invoice.attributes.status) {
    case InvoiceStatus.Draft: {
      return (
        <StickerContainer>
          <Sticker variant='caution'>Draft</Sticker>
        </StickerContainer>
      );
    }
    case InvoiceStatus.Open: {
      return (
        <StickerContainer>
          <Sticker variant='caution'>Open</Sticker>
        </StickerContainer>
      );
    }
    case InvoiceStatus.Paid: {
      return (
        <StickerContainer>
          <Sticker variant='success'>Paid</Sticker>
        </StickerContainer>
      );
    }
    case InvoiceStatus.Uncollectible: {
      return (
        <StickerContainer>
          <Sticker variant='danger'>Uncollectible</Sticker>
        </StickerContainer>
      );
    }
    case InvoiceStatus.Void: {
      return (
        <StickerContainer>
          <Sticker variant='basic'>Void</Sticker>
        </StickerContainer>
      );
    }
    case InvoiceStatus.Canceled: {
      return (
        <StickerContainer>
          <Sticker variant='basic'>Canceled</Sticker>
        </StickerContainer>
      );
    }
    default:
      return null;
  }
};

const DashboardOrganizationBillingInvoicesView = ({
  invoices,
  loadStripeInvoicePdfUrl,
}: DashboardOrganizationBillingInvoicesViewProps) => {
  const [usageDetailsModal, { enable: showUsageDetailModal, disable: hideUsageDetailModal }] =
    useToggle(false);
  const [invoiceId, setInvoiceId] = useState<string | undefined>(undefined);
  const showUsageDetailsData = (id: string) => {
    setInvoiceId(id);
    showUsageDetailModal();
  };

  const selectedInvoice = invoices.find((invoice) => {
    return invoice.id === invoiceId;
  });

  const invoiceRows = sortBy([(inv) => inv.attributes['period-start-at']], invoices)
    .reverse()
    .map((invoice) => {
      const periodStartAtDisplay = formatBillingDate(invoice.attributes['period-start-at']);
      const periodEndAtDisplay = formatBillingDate(invoice.attributes['period-end-at']);
      const billingPeriodDisplay = `${periodStartAtDisplay} — ${periodEndAtDisplay}`;

      const handleClick = () => {
        showUsageDetailsData(invoice.id);
      };

      const handleDownload = async (invoice: Invoice) => {
        const stripeInvoicePdfUrl = await loadStripeInvoicePdfUrl(invoice);
        window.open(stripeInvoicePdfUrl);
      };

      return (
        <TableTR
          onClick={handleClick}
          key={invoice.id}
          id={`invoices-table__row--${invoice.id}`}
          data-test={`invoices-table__row--${invoice.id}`}
        >
          <TableTD>
            <InvoiceStatusPill invoice={invoice} />
          </TableTD>
          <TableTD>{invoice.id}</TableTD>
          <TableTD>{billingPeriodDisplay}</TableTD>
          <TableTD>
            {invoice.attributes['total-amount-cents']
              ? formatDollar(invoice.attributes['total-amount-cents'] / 100)
              : '-'}
          </TableTD>
          <TableTD>
            {invoice.attributes['pdf-url'] && (
              <Button
                id='stripe-invoice__btn-download'
                data-test='stripe-invoice__btn-download'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownload(invoice);
                }}
                width='icon'
                variant='primary'
              >
                <TransferInIcon />
              </Button>
            )}
          </TableTD>
        </TableTR>
      );
    });

  return (
    <Card>
      <Card.Body>
        <Card.Title>Invoices</Card.Title>
        <TableLayout
          dataTest='invoices-table'
          showHeader={true}
          isEmpty={invoices.length === 0}
          nullState={
            <TableNullState dataTestBlock='invoices-view'>
              <div>Looks like you don&rsquo;t have any invoices yet.</div>
            </TableNullState>
          }
          headerTR={
            <>
              <TableTH data-test='invoices-table__status'>Status</TableTH>
              <TableTH data-test='invoices-table__status'>Invoice ID</TableTH>
              <TableTH data-test='invoices-table__billing-period'>Billing Period (UTC)</TableTH>
              <TableTH data-test='invoices-table__total-amount'>Total Amount</TableTH>
              <TableTH data-test='invoices-table__download'>Download</TableTH>
            </>
          }
        >
          {invoiceRows}
        </TableLayout>
        <ModalWrapper isOpen={usageDetailsModal} handleCloseModal={hideUsageDetailModal}>
          <ModalContainer size='lg'>
            <ModalHeader onClose={hideUsageDetailModal}>Usage Details</ModalHeader>
            <ModalBody>
              {selectedInvoice ? (
                <DashboardOrganizationInvoiceFinalizedView
                  invoices={invoices}
                  selectedInvoice={selectedInvoice}
                />
              ) : (
                <Card>
                  <Card.Body>
                    <Paragraph>
                      We&rsquo;re putting the finishing touches on your usage data. We&rsquo;ll
                      populate this tab as soon as it&rsquo;s ready.
                    </Paragraph>
                  </Card.Body>
                </Card>
              )}
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>
      </Card.Body>
    </Card>
  );
};

const StickerContainer = styled('div')`
  max-width: 400px;
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;

  grid-column-gap: ${Spacing[2]};
  grid-row-gap: ${Spacing[2]};
`;

export default DashboardOrganizationBillingInvoicesView;
